import React, { useState, useEffect, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ChevronRight,
  ChevronDown,
  Search,
  Play,
  Eye,
  Zap,
  Filter,
} from 'lucide-react';
import {
  StrategyConfig,
  StrategyCategory,
  STRATEGY_CATEGORIES,
  strategyApiService,
} from '@/services/strategyConfigService';

// ── Props ──────────────────────────────────────────────────

interface StrategyPresetBrowserProps {
  onSelect: (strategy: StrategyConfig) => void;
  onEnable: (strategy: StrategyConfig) => void;
  selectedId?: string;
}

// ── Component ──────────────────────────────────────────────

export const StrategyPresetBrowser: React.FC<StrategyPresetBrowserProps> = ({
  onSelect,
  onEnable,
  selectedId,
}) => {
  const [presets, setPresets] = useState<Record<string, StrategyConfig[]>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['Bullish', 'Bearish'])
  );

  // Load presets
  useEffect(() => {
    setLoading(true);
    strategyApiService
      .getPresets()
      .then(setPresets)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Search filter
  const filteredPresets = useMemo(() => {
    if (!search.trim()) return presets;
    const q = search.toLowerCase();
    const result: Record<string, StrategyConfig[]> = {};
    for (const [cat, strategies] of Object.entries(presets)) {
      const filtered = strategies.filter(
        s =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          s.alerts.some(a => a.toLowerCase().includes(q))
      );
      if (filtered.length > 0) result[cat] = filtered;
    }
    return result;
  }, [presets, search]);

  const toggleExpand = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const getCategoryMeta = (cat: string) =>
    STRATEGY_CATEGORIES.find(c => c.value === cat) ?? {
      value: cat,
      label: cat,
      color: 'bg-gray-600',
      icon: '📋',
    };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        Loading presets...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Search strategies..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Category tree */}
      <div className="space-y-1">
        {Object.entries(filteredPresets).map(([cat, strategies]) => {
          const meta = getCategoryMeta(cat);
          const expanded = expandedCategories.has(cat) || search.trim().length > 0;

          return (
            <div key={cat} className="border border-border rounded-md overflow-hidden">
              {/* Category header */}
              <div
                className="flex items-center gap-2 px-3 py-2 bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors select-none"
                onClick={() => toggleExpand(cat)}
              >
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                )}
                <span className="text-base">{meta.icon}</span>
                <span className="text-sm font-semibold flex-1">{meta.label}</span>
                <Badge variant="outline" className="text-[10px] h-5">
                  {strategies.length}
                </Badge>
              </div>

              {/* Strategy list */}
              {expanded && (
                <div className="divide-y divide-border/40">
                  {strategies.map(strategy => {
                    const isSelected = strategy.id === selectedId;
                    return (
                      <div
                        key={strategy.id}
                        className={`px-3 py-2.5 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-primary/10 border-l-2 border-l-primary'
                            : 'hover:bg-muted/30 border-l-2 border-l-transparent'
                        }`}
                        onClick={() => onSelect(strategy)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">
                              {strategy.name}
                            </div>
                            <p className="text-[11px] text-muted-foreground/70 mt-0.5 line-clamp-2">
                              {strategy.description}
                            </p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                                <Zap className="h-2.5 w-2.5" />
                                {strategy.alerts.length} alerts
                              </span>
                              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                                <Filter className="h-2.5 w-2.5" />
                                {strategy.filters.length} filters
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              title="View details"
                              onClick={e => {
                                e.stopPropagation();
                                onSelect(strategy);
                              }}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="default"
                              size="icon"
                              className="h-7 w-7"
                              title="Enable strategy"
                              onClick={e => {
                                e.stopPropagation();
                                onEnable(strategy);
                              }}
                            >
                              <Play className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(filteredPresets).length === 0 && (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No strategies found
        </div>
      )}
    </div>
  );
};

export default StrategyPresetBrowser;
